<template>
  <v-card class="pt-2 pb-4 pl-5 pr-5 mt-3">
    <v-dialog v-model="showAccountDialog"
              max-width="500"
              @input="v => v">
      <v-card class="pa-4">
        <v-layout column>
          <span class="title">Add Bank Account</span>
          <form data-vv-scope="bankAccount">
            <v-flex class="mt-2">
              <v-flex>
                <v-text-field v-model="holderName"
                              :error-messages="errors.first('holderName', 'bankAccount')"
                              data-vv-as="Holder Name"
                              data-vv-name="holderName"
                              data-vv-scope="bankAccount"
                              v-validate="'required'"
                              label="Holder Name">
                </v-text-field>

                <v-text-field v-model="routingNumber"
                              :error-messages="errors.first('routingNumber', 'bankAccount')"
                              data-vv-as="Routing Number"
                              data-vv-name="routingNumber"
                              data-vv-scope="bankAccount"
                              v-validate="'required|digits:9'"
                              label="Routing Number">
                </v-text-field>

                <v-text-field v-model="accountNumber"
                              :error-messages="errors.first('accountNumber', 'bankAccount')"
                              data-vv-as="Account Number"
                              data-vv-name="accountNumber"
                              data-vv-scope="bankAccount"
                              v-validate="'required|numeric'"
                              label="Account Number"></v-text-field>

                <v-radio-group v-model="holderType">
                  <div slot="label">Holder Type</div>
                  <v-radio label="Company" value="company"></v-radio>
                  <v-radio label="Individual" value="individual"></v-radio>
                </v-radio-group>
              </v-flex>
            </v-flex>

            <v-card-actions>
              <v-spacer />
              <v-btn color="accent" @click="saveAccount"
                     :disabled="errors.any('bankAccount')">
                Save
              </v-btn>
              <v-btn color="accent" flat @click="showAccountDialog = false">Cancel</v-btn>
            </v-card-actions>
          </form>
        </v-layout>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showCardDialog"
              max-width="500"
              @input="v => v">
      <v-card class="pa-4">
        <v-layout column>
          <span class="title">Add Credit Card</span>

          <v-text-field v-model="cardHolderName"
                        :error-messages="errors.first('holderName', 'card')"
                        data-vv-as="Cardholder Name"
                        data-vv-name="holderName"
                        data-vv-scope="card"
                        v-validate="'required'"
                        label="Cardholder Name">
          </v-text-field>
          <div ref="card-form" class="py-4"></div>
          <v-card-actions>
            <v-spacer />
            <v-btn color="accent" @click="saveCard">Save</v-btn>
            <v-btn color="accent" flat @click="showCardDialog = false">Cancel</v-btn>
          </v-card-actions>
        </v-layout>
      </v-card>
    </v-dialog>

    <v-layout align-baseline>
      <div class="title mr-3 mt-4">
        Bank Accounts
        <v-btn fab small color="accent"
               @click="showAccountDialog = true">
          <v-icon dark>add</v-icon>
        </v-btn>
      </div>
    </v-layout>

    <v-data-table :headers="bankHeaders"
                  :pagination.sync="pagination"
                  hide-actions
                  disable-initial-sort
                  :items="accounts">
      <template slot="items" slot-scope="slotItems">
        <tr>
          <td>
            {{slotItems.item.account_holder_name}}
          </td>
          <td>
            {{slotItems.item.routing_number}}
          </td>
          <td>
            ########{{slotItems.item.last4}}
          </td>
          <td>
            {{slotItems.item.bank_name}}
          </td>
          <td>
            {{slotItems.item.account_holder_type}}
          </td>
          <td>
            <!--v-btn flat small>
              Primary method
            </v-btn -->
            <v-chip v-if="customer.default_source === slotItems.item.id"
                    color="green" text-color="white">
              <v-avatar>
                <v-icon>check_circle</v-icon>
              </v-avatar>
              Primary Payment Method
            </v-chip>

            <v-btn v-else
                   @click="setPrimaryMethod(slotItems.item.id)"
                   :loading="loadingBtn"
                   small color="primary">
              Set as primary method
            </v-btn>

            <v-btn @click="deleteAccount(slotItems.item.id)"
                   :disabled="customer.default_source === slotItems.item.id"
                   :loading="loadingBtn"
                   flat small color="accent">
              Remove
            </v-btn>
          </td>
        </tr>
      </template>
    </v-data-table >

    <v-layout align-baseline>
      <div class="title mr-3 mt-4">
        Credit Cards
        <v-btn fab small color="accent"
               @click="showCardDialog = true">
          <v-icon dark>add</v-icon>
        </v-btn>
      </div>
    </v-layout>

    <v-data-table :headers="cardsHeaders"
                  :pagination.sync="pagination"
                  hide-actions
                  disable-initial-sort
                  :items="cards">
      <template slot="items" slot-scope="slotItems">
        <tr>
          <td>
            {{slotItems.item.name}}
          </td>
          <td>
            {{slotItems.item.brand}}
          </td>
          <td>
            ####-####-####-{{slotItems.item.last4}}
          </td>
          <td>
            <v-chip v-if="customer.default_source === slotItems.item.id"
                    color="green" text-color="white">
              <v-avatar>
                <v-icon>check_circle</v-icon>
              </v-avatar>
              Primary Payment Method
            </v-chip>

            <v-btn v-else
                   @click="setPrimaryMethod(slotItems.item.id)"
                   :loading="loadingBtn"
                   small color="primary">
              Set as primary method
            </v-btn>

            <v-btn flat
                   :disabled="customer.default_source === slotItems.item.id"
                   @click="deleteAccount(slotItems.item.id)"
                   :loading="loadingBtn"
                   small color="accent">
              Remove
            </v-btn>
          </td>
        </tr>
      </template>
    </v-data-table >
  </v-card>
</template>

<script>
import _ from 'lodash';
import API from '@/api';

const stripe = window.Stripe(`${process.env.STRIPE_PUBLIC_KEY}`);
const CUSTOMER_ID = 'cus_E5q5SyC2aHBdVL'; // TODO: use company Customer Id

export default {
  name: 'PaymentMethodsSection',
  props: {
    company: {
      type: Object,
    },
  },
  data() {
    return {
      card: null,
      members: [],
      customer: null,
      showAccountDialog: false,
      showCardDialog: false,
      loadingBtn: false,
      cardHolderName: null,
      holderType: 'company',
      holderName: null,
      accountNumber: null,
      routingNumber: null,
      primaryMethod: false,
      CVC: null,
      pagination: {
        sortBy: 'created',
        descending: true,
        rowsPerPage: -1, // -1 for All
      },
      bankHeaders: [
        { text: 'Account Holder', value: 'account_holder_name' },
        { text: 'Routing Number', value: 'routing_number' },
        { text: 'Account Number', value: 'last4' },
        { text: 'Bank Name', value: 'bank_name' },
        { text: 'Type', value: 'number' },
        { text: ' ', value: 'actions', sortable: false },
      ],
      cardsHeaders: [
        { text: 'Cardholder Name', value: 'name' },
        { text: 'Brand', value: 'brand' },
        { text: 'Card Number', value: 'last4' },
        { text: ' ', value: 'actions', sortable: false },
      ],
    };
  },
  computed: {
    accounts() {
      let accounts = [];
      if (this.customer) {
        accounts = _.filter(_.get(this.customer, 'sources.data', []), { object: 'bank_account' });
      }

      return accounts;
    },
    cards() {
      let cards = [];
      if (this.customer) {
        cards = _.filter(_.get(this.customer, 'sources.data', []), { object: 'card' });
      }

      return cards;
    },
  },
  watch: {
    company() {
      this.loadCustomerInfo();
    },
    showAccountDialog(val) {
      if (!val) {
        this.holderType = 'company';
        this.holderName = null;
        this.accountNumber = null;
        this.routingNumber = null;
      }
    },
    showCardDialog(val) {
      if (!val) {
        this.cardHolderName = null;
        this.card.clear();
      }
    },
  },
  methods: {
    async saveAccount() {
      const isValid = await this.$validator.validateAll('bankAccount');
      if (isValid) {
        const { token, error } = await stripe.createToken('bank_account', {
          country: 'US',
          currency: 'usd',
          routing_number: this.routingNumber,
          account_number: this.accountNumber,
          account_holder_name: this.holderName,
          account_holder_type: this.holderType,
        });

        if (error) {
          this.$store.dispatch('applications/setErrorNotification', {
            message: error.message,
          });
        } else {
          try {
            await API.stripe.createSource({
              customerId: CUSTOMER_ID,
              body: {
                source: token.id,
              },
            });

            this.loadCustomerInfo();
            this.showAccountDialog = false;
          } catch (e) {
            this.$store.dispatch('applications/setErrorNotification', {
              message: e.error.message,
            });
          }
        }
      }
    },
    async deleteAccount(accountId) {
      this.loadingBtn = true;
      await API.stripe.deleteSource({ customerId: CUSTOMER_ID, accountId });
      this.loadingBtn = false;
      this.loadCustomerInfo();
    },
    async saveCard() {
      const isValid = await this.$validator.validateAll('card');
      if (isValid) {
        const { token, error } = await stripe.createToken(this.card, { name: this.cardHolderName });

        if (error) {
          this.$store.dispatch('applications/setErrorNotification', {
            message: error.message,
          });
        } else {
          try {
            await API.stripe.createSource({
              customerId: CUSTOMER_ID,
              body: {
                source: token.id,
              },
            });

            this.loadCustomerInfo();
            this.showCardDialog = false;
          } catch (e) {
            this.$store.dispatch('applications/setErrorNotification', {
              message: e.error.message,
            });
          }
        }
      }
    },
    async loadCustomerInfo() {
      const apiResponse = await API.stripe.getCustomer({ customerId: CUSTOMER_ID });
      this.customer = apiResponse.data;
    },
    async setPrimaryMethod(sourceId) {
      this.loadingBtn = true;
      await API.stripe.updateCustomer({
        customerId: CUSTOMER_ID,
        body: { default_source: sourceId },
      });

      this.loadCustomerInfo();
      this.loadingBtn = false;
    },
  },
  created() {
    this.loadCustomerInfo();
  },
  mounted() {
    const style = {
      iconStyle: 'solid',
      style: {
        base: {
          iconColor: '#32325d',
          color: '#32325d',
          fontWeight: 400,
          fontSize: '16px',
          fontSmoothing: 'antialiased',

          '::placeholder': {
            color: 'rgba(0,0,0,.54)',
          },
          ':-webkit-autofill': {
            color: '#fce883',
          },
        },
      },
    };

    const elements = stripe.elements();
    this.card = elements.create('card', style);
    this.card.mount(this.$refs['card-form']);
  },
};
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/pages/Companies/Sections/PaymentMethodsSection.vue